// src/components/home/HomeAttention.tsx
import React from 'react';
import { AlertTriangle, MessageCircle, CheckCircle2 } from 'lucide-react';

import { getInitials } from '@/lib/avatarUtils';
import { getWhatsAppUrl } from '@/utils/whatsapp';

export type AtRiskClient = {
    id: string;
    name: string;
    phone?: string | null;
    days: number;
};

type Props = {
    atRisk: AtRiskClient[];
};

const HomeAttention: React.FC<Props> = ({ atRisk }) => {
    const visible = atRisk.slice(0, 5);

    return (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-8 h-full">
            <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-500 flex items-center justify-center">
                    <AlertTriangle size={20} />
                </div>
                <div>
                    <h2 className="text-lg font-bold text-gray-900 leading-none">Richiede attenzione</h2>
                    <p className="text-sm text-gray-400 mt-1">Clienti da richiamare</p>
                </div>
            </div>

            {atRisk.length === 0 ? (
                <div className="flex items-center gap-3 p-4 rounded-2xl bg-green-50/60 text-green-700">
                    <CheckCircle2 size={22} />
                    <div>
                        <p className="font-bold text-sm">Tutto sotto controllo</p>
                        <p className="text-xs text-green-600/80">Nessun cliente da richiamare al momento.</p>
                    </div>
                </div>
            ) : (
                <>
                    <p className="text-sm text-gray-500 mb-3">
                        <span className="font-bold text-gray-900">{atRisk.length}</span>{' '}
                        {atRisk.length === 1 ? 'cliente non torna da un po’' : 'clienti non tornano da un po’'}
                    </p>
                    <div className="space-y-2">
                        {visible.map((c) => {
                            const waUrl = getWhatsAppUrl(c.phone);
                            return (
                                <div
                                    key={c.id}
                                    className="flex items-center gap-3 p-3 rounded-2xl border border-gray-100 hover:border-gray-200 hover:bg-gray-50/60 transition-colors"
                                >
                                    <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-sm font-bold uppercase shrink-0">
                                        {getInitials(c.name)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-gray-800 text-sm truncate">{c.name || 'Cliente'}</p>
                                        <p className="text-xs text-gray-400">Non torna da {c.days} giorni</p>
                                    </div>
                                    {waUrl && (
                                        <a
                                            href={waUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Richiama su WhatsApp"
                                            className="flex items-center gap-1.5 bg-[#25D366] hover:bg-[#20bd5a] text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors active:scale-95 shrink-0"
                                        >
                                            <MessageCircle size={15} /> Richiama
                                        </a>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {atRisk.length > visible.length && (
                        <p className="text-xs text-gray-400 mt-3 text-center">
                            e altri {atRisk.length - visible.length} clienti da richiamare
                        </p>
                    )}
                </>
            )}
        </div>
    );
};

export default HomeAttention;
